import React, { useCallback, useMemo, useState } from 'react';
import { useCountryData } from '../hooks/useCountryData';
import { MetricId, metricDomain, metricsForScenario } from '../utils/mapMetrics';
import { useScenario } from '../contexts/ScenarioContext';
import { MAP_RAMPS, rampLegend } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import { formatCompact, formatPct } from '../utils/format';
import { CountryRow } from '../types/census';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { SourceRef } from '../components/SourceRef';
import { WorldMap } from '../components/map/WorldMap';
import { CountryPanel } from '../components/map/CountryPanel';
import { COMPILED_LABEL } from '../data/sources';
import { PageSources } from '../components/PageSources';
export function MapPage() {
  const data = useCountryData();
  const [metricId, setMetricId] = useState<MetricId>('internet');
  const [selected, setSelected] = useState<CountryRow | null>(null);
  const [rendered, setRendered] = useState<number | null>(null);
  const {
    scenarioId,
    scenario
  } = useScenario();
  const {
    theme
  } = useTheme();
  const mapMetrics = useMemo(() => metricsForScenario(scenarioId), [scenarioId]);
  const metric = mapMetrics.find(m => m.id === metricId) ?? mapMetrics[0];
  const rowsByCcn3 = useMemo(() => {
    const m = new Map<string, CountryRow>();
    data.rows.forEach(r => r.ccn3 && m.set(r.ccn3, r));
    return m;
  }, [data.rows]);
  const domain = useMemo(() => metricDomain(metric, data.rows), [metric, data.rows]);
  const totals = useMemo(() => {
    const pop = data.rows.reduce((s, r) => s + r.population, 0);
    const withNet = data.rows.filter(r => r.internetPct);
    const netPop = withNet.reduce((s, r) => s + r.population, 0);
    const avg = netPop > 0 ? withNet.reduce((s, r) => s + r.population * (r.internetPct?.value ?? 0), 0) / netPop : null;
    return {
      pop,
      avg,
      entries: data.rows.length
    };
  }, [data.rows]);
  const mode = metric.modeKey === 'median' || metric.modeKey === 'curated' ? 'snapshot' : data.loading ? 'loading' : data.modes[metric.modeKey];
  const asOf = metric.modeKey === 'median' ? 'WPP 2024' : metric.modeKey === 'curated' ? COMPILED_LABEL : data.asOf[metric.modeKey];
  const onRendered = useCallback((n: number) => setRendered(n), []);
  const close = useCallback(() => setSelected(null), []);
  return <>
      <PageHeader title="World Map" lede="Gall–Peters equal-area projection — every country drawn at its true relative size, not Mercator. Colour it by people, connectivity, agents, or the AI ecosystem: who builds and who releases models." data-id="element-1233" data-mp="2jd12bprwh4sl" />

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-1234" data-mp="2x3mkfc9j5apf">
        <dl className="grid grid-cols-3 border-b border-line" data-id="element-1235" data-mp="323seez39v9ao">
          <div className="py-5 pr-4" data-id="element-1236" data-mp="325lqitwnmlq2">
            <dt className="text-[12.5px] text-muted" data-id="element-1237" data-mp="372lw73pldmgn">Countries rendered</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-1238" data-mp="370iwfryn0eyq">
              {rendered ?? '—'}
              <SourceRef id="worldAtlas" data-id="element-1239" data-mp="3c7pf2jihkg45" />
            </dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-1240" data-mp="3c9sd9qtslfjy">
            <dt className="text-[12.5px] text-muted" data-id="element-1241" data-mp="379nxk30pkous">Total population summed</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-1242" data-mp="337suou34q3ew">
              {data.loading ? '—' : formatCompact(totals.pop)}
              <SourceRef id="restcountries" data-id="element-1243" data-mp="3r49zdsz676b" />
            </dd>
            <dd className="font-mono text-[11px] text-faint" data-id="element-1244" data-mp="3uz8eyhh7re1y">{totals.entries} entries</dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-1245" data-mp="3q6fck097wht1">
            <dt className="text-[12.5px] text-muted" data-id="element-1246" data-mp="3m653geq4pb9y">Global internet-user average</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-1247" data-mp="midlfsmb360j">
              {totals.avg != null ? formatPct(totals.avg) : '—'}
              <SourceRef id="wbInternet" data-id="element-1248" data-mp="hqvkv5o737g3" />
            </dd>
            <dd className="font-mono text-[11px] text-faint" data-id="element-1249" data-mp="29xawb962sdb">population-weighted</dd>
          </div>
        </dl>

        <div className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-1250" data-mp="799y4x3ya1gf">
          <div role="radiogroup" aria-label="Color the map by" className="flex flex-wrap gap-1.5" data-id="element-1251" data-mp="3pev9866ds61y">
            {mapMetrics.map(m => <button key={m.id} type="button" role="radio" aria-checked={m.id === metricId} onClick={() => setMetricId(m.id)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] transition-[background-color,border-color,color] duration-150 ease-out ${m.id === metricId ? 'border-machine bg-machine/10 text-ink' : 'border-line text-muted hover:border-faint hover:text-ink'}`} data-id="element-1252" data-mp="3tggtwdp5pomp">
                {m.label}
              </button>)}
          </div>
        </div>
        <PageSources data-id="element-1253" data-mp="2tg50zemdyu3x">
          <DataBadge mode={mode} source={`${metric.sourceLabel} · ${metric.label}`} asOf={asOf} data-id="element-1254" data-mp="2tfmsnjmg8pfn" />
        </PageSources>

        <WorldMap rowsByCcn3={rowsByCcn3} metric={metric} domain={domain} selected={selected?.cca3 ?? null} onSelect={setSelected} onRendered={onRendered} data-id="element-1255" data-mp="2og0kholh9zdp" />

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between" data-id="element-1256" data-mp="2y6v2ggroyctc">
          <div className="w-full max-w-md" data-id="element-1257" data-mp="2ahan07uqxxot">
            <div className="flex items-center justify-between font-mono text-[11px] text-muted" data-id="element-1258" data-mp="2ai246dnljsvk">
              <span data-id="element-1259" data-mp="25jhfnbhpixmy">{data.loading ? '—' : metric.format(domain[0])}</span>
              <span className="text-ink" data-id="element-1260" data-mp="2kfzud6p0nzqt">
                {metric.label}
                {metric.id === 'agents' ? ` · ${scenario.label}` : ''}
                {metric.scale === 'log' ? ' · log scale' : ''}
                <SourceRef id={metric.sourceId} estimate={metric.estimate ? {
                low: '−15%',
                high: '+15%'
              } : undefined} data-id="element-1261" data-mp="3vtu475x8qay3" />
              </span>
              <span data-id="element-1262" data-mp="3qv95xc1ju1ca">{data.loading ? '—' : metric.format(domain[1])}</span>
            </div>
            <div className="mt-1.5 h-2.5 rounded-sm" style={{
            background: rampLegend(MAP_RAMPS[theme])
          }} aria-hidden="true" data-id="element-1263" data-mp="1stbla26v1il" />
          </div>
          <p className="flex items-center gap-2 text-[12px] text-muted" data-id="element-1264" data-mp="brs7rt5rp4op">
            <span className="h-2.5 w-4 rounded-sm bg-raised ring-1 ring-line" aria-hidden="true" data-id="element-1265" data-mp="bpfptg00eubn" /> No data · values are also listed in tooltips, the panel and the country table
          </p>
        </div>
        <p className="mt-3 text-[12px] text-muted" data-id="element-1266" data-mp="3f6efc5m7zaxi">
          India is shown with its official boundaries, including all of Jammu &amp; Kashmir and Ladakh (Aksai Chin) and Arunachal Pradesh.
          <SourceRef id="surveyOfIndia" data-id="element-1267" data-mp="3p4c4cvbwlbr6" />
          <span className="ml-1" data-id="element-1268" data-mp="3txnep6fxcanu">Boundaries are simplified to the map’s 1:110m scale.</span>
        </p>
      </section>

      <CountryPanel row={selected} modes={data.modes} onClose={close} data-id="element-1269" data-mp="2wgop7vtejdbx" />
    </>;
}